import React from'react';
import { useLayout } from'../components/Layout';
import { useFilters } from'../context/FilterContext';
import { useApiQuery } from'../hooks/useApi';
import api from'../api/client';
import KPICard from'../components/charts/KPICard';
import AreaChart from'../components/charts/AreaChart';
import BarChart from'../components/charts/BarChart';
import PieChart from'../components/charts/PieChart';
import ExportButton from'../components/ExportButton';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import PageLoader from'../components/PageLoader';
import {
 TrendingUp, Calculator, Layers, Activity, Cpu,
 Server, X, ChevronRight, Search, AlertTriangle,
} from'lucide-react';

/* ─────────────────────────────────────────────────────────────────────────
 Cost Center (overhauled)
 ----------------------------------------------------------------------
 Goal: a focused, modern observability dashboard answering five
 questions at a glance —

 1. How much are we spending on Foundry / models?
 2. How many tokens / calls is that buying?
 3. What's our blended cost per 1K tokens?
 4. Where are we trending (projected spend for this window)?
 5. Which Foundry resources drive that spend, and which models
 inside each resource?

 Everything else from the old page (APIM gateway KPI, APIM-subscription
 attribution table, per-call ratios, the stacked-by-category area
 chart, the standalone forecast chart) has been removed — those views
 are either redundant with the per-resource detail panel or were
 surfacing dimensions Cost Management can't authoritatively split.
 ───────────────────────────────────────────────────────────────────── */

// Cost Management has 8–24h ingestion latency and 1h backend cache;
// matching the refetch interval avoids wasting the upstream quota.
const COST_REFETCH_MS = 15 * 60 * 1000;

// Approximate day count per timeRange token, used to scale the
// run-rate projection. Sub-day tokens collapse to 1 — Cost Management
// can't serve them anyway, so the"projection" for those is just the
// actual figure unchanged.
const RANGE_DAYS = {
'5m': 1,'15m': 1,'1h': 1,'6h': 1,'24h': 1,
'7d': 7,'30d': 30,'90d': 90,
};

export default function CostCenter() {
 const { timeRange } = useLayout();

 // ── Data fetches ──────────────────────────────────────────────────
 // /breakdown gives us the headline AI cost, the daily series, and the
 // per-model rollup we need for both the daily cost chart and the
 // top-line KPIs.
 const { data: breakdown, isPending: breakdownPending } = useApiQuery(
 ['cost-breakdown', timeRange],
 () => api.getCostBreakdown(timeRange),
 { refetchInterval: COST_REFETCH_MS }
 );

 // Per-resource detail with embedded byModel and daily series in a
 // single round-trip. Drives the master list and the detail panel.
 const { data: byResource } = useApiQuery(
 ['cost-by-resource', timeRange],
 () => api.getCostByResource(timeRange),
 { refetchInterval: COST_REFETCH_MS }
 );

 // Estimated cost per APIM Product (tokens × pricing). This is COMPUTED,
 // not billed — Cost Management can't break down by product — so the
 // section is clearly labelled"estimated".
 const { data: byProduct } = useApiQuery(
 ['cost-by-product', timeRange],
 () => api.getCostByProduct(timeRange),
 { refetchInterval: COST_REFETCH_MS }
 );

 // Actual-vs-forecast spend for the whole resource group. Uses Azure Cost
 // Management's own forecast (billed-accurate, up to 364 days of history +
 // a future projection), so it works over long ranges where the 45-day
 // gateway-log estimate can't. Drives the Cost Trend forecast overlay and
 // the projected month-end KPI.
 const { data: forecast } = useApiQuery(
 ['cost-forecast', timeRange],
 () => api.getCostForecast(timeRange, 30),
 { refetchInterval: COST_REFETCH_MS }
 );

 // ── Derived figures ───────────────────────────────────────────────
 const byModelRows = React.useMemo(
 () => (Array.isArray(breakdown?.byModel) ? breakdown.byModel : []),
 [breakdown]
 );

 const foundryCost = React.useMemo(
 () => byModelRows.reduce((s, m) => s + (Number(m.Cost) || 0), 0),
 [byModelRows]
 );
 const totalTokens = React.useMemo(
 () => byModelRows.reduce((s, m) => s + (m.TotalTokens || 0), 0),
 [byModelRows]
 );
 const totalCalls = React.useMemo(
 () => byModelRows.reduce((s, m) => s + (m.Calls || m.CallCount || 0), 0),
 [byModelRows]
 );
 const costPer1KTokens = totalTokens > 0 ? (foundryCost / totalTokens) * 1000 : null;

 // ── Projection: run-rate scaled to the selected window ────────────
 //
 // The user asked for a projection that"scales correctly for the
 // selected time range". We take the daily series we already have,
 // compute the actual-days-elapsed avg, and project it forward to
 // fill out the full window. Two cases:
 //
 // • If the window has only partial days of data (very recent
 // window with one or two days missing from CM ingestion), we
 // project the missing days at the run-rate so the headline
 // number represents"expected total for this window".
 // • If the window is fully populated, run-rate × window-days
 // equals the actual — so the KPI just mirrors the Foundry Cost.
 // Still useful as a sanity check.
 //
 // We deliberately don't use Azure's calendar-month forecast here:
 // it always reports a month-end total regardless of the window the
 // user picked, which is exactly the bug the user flagged.
 const dailySeries = React.useMemo(() => {
 // /breakdown returns`daily` (one row per day+model); collapse to
 // pure daily totals here so the chart and projection share input.
 const rows = Array.isArray(breakdown?.daily) ? breakdown.daily : [];
 const byDate = {};
 for (const r of rows) {
 const date = (r.timestamp || r.TimeGenerated ||'').slice(0, 10);
 if (!date) continue;
 byDate[date] = (byDate[date] || 0) + (Number(r.Cost) || 0);
 }
 return Object.entries(byDate)
 .map(([date, cost]) => ({
 date,
 // Match the AreaChart prop name; the X-axis formatter expects
 // a parseable date string.
 TimeSlice:`${date}T00:00:00Z`,
 Cost: Math.round(cost * 1000000) / 1000000,
 }))
 .sort((a, b) => a.date.localeCompare(b.date));
 }, [breakdown]);

 const projectedCost = React.useMemo(() => {
 const windowDays = RANGE_DAYS[timeRange] || 30;
 const daysWithData = dailySeries.filter((d) => d.Cost > 0).length;
 if (daysWithData === 0) return foundryCost; // can't run-rate from zeros
 const avgDaily = foundryCost / daysWithData;
 return avgDaily * windowDays;
 }, [dailySeries, foundryCost, timeRange]);

 // ── Notice banner (clamped range / unavailable upstream) ──────────
 // Less noisy than the previous three-banner cascade — one line, one
 // colour, the most pressing message wins.
 const isClamped = !!breakdown?.clamped;
 const clampedRequested = breakdown?.requestedTimeRange || null;
 const clampedEffective = breakdown?.timeRange ||'24h';
 const isUnavailable = !!breakdown?.unavailable;
 const unavailableReason = breakdown?.unavailableReason;
 // Where the dollar figures came from. For short ranges (<=24h) the backend
 // prices token usage instead of using Cost Management actuals, so dollars
 // and usage come from the same window — surfaced as 'estimate'.
 const costSource = breakdown?.costSource ||'actual';
 const isEstimated = costSource !=='actual';

 // Detect"genuinely empty window" — no KQL token traces AND no ARM
 // dollar rows. Most commonly seen on the 24h view in low-traffic
 // environments (lab/dev tenants where activity is older than 24h)
 // and would otherwise render as an empty page that looks broken.
 // Surface a friendly message before any upstream-availability noise
 // so users aren't misled into thinking ARM throttling is the cause
 // when there's simply no activity to report.
 const hasAnyData = byModelRows.length > 0 || (Array.isArray(breakdown?.daily) && breakdown.daily.length > 0);

 let notice = null;
 if (breakdown && !hasAnyData) {
 notice = {
 level:'info',
 text:`No model activity in the last ${timeRange}. Widen the time range or check the Conversations page for the most recent calls.`,
 };
 } else if (isUnavailable && unavailableReason !=='stale') {
 notice = {
 level:'error',
 text:'Azure Cost Management is unreachable. Cost figures may be incomplete — try again in a few minutes.',
 };
 } else if (isUnavailable && unavailableReason ==='stale') {
 notice = {
 level:'warn',
 text:'Azure Cost Management is rate-limiting requests. Showing the last cached data, which may be slightly stale.',
 };
 } else if (isEstimated) {
 notice = {
 level:'info',
 text:`Cost Management is daily-grained with 8–24h lag, so for short ranges the dollar figures are estimated from token usage × model pricing (not billed). Pick 7d or longer for Azure-billed actuals.`,
 };
 } else if (isClamped) {
 notice = {
 level:'info',
 text:`Cost Management is daily-grained with 8–24h ingestion lag, so figures reflect the last ${clampedEffective}${clampedRequested ?` (you picked ${clampedRequested})` :''}.`,
 };
 }
 // Note: we deliberately do NOT infer Log Analytics retention from the
 // span of returned rows. That heuristic conflated"low traffic"
 // (workspace retains 2y but oldest call was 6d ago) with"retention
 // shortfall" (workspace only keeps 7d of traces), and there is no
 // way to disambiguate the two from KQL alone — the workspace's
 // retention setting lives in ARM/portal config. If we ever want to
 // surface a genuine retention warning, fetch the workspace
 // retentionInDays from the ARM management endpoint and compare to
 // the selected window, not to the oldest observed row.

 // ── Resource master/detail state ──────────────────────────────────
 const resources = React.useMemo(
 () => (Array.isArray(byResource?.data) ? byResource.data : []),
 [byResource]
 );

 // Auto-select the top resource once data arrives so the detail panel
 // never sits empty. The user can dismiss it via the close button.
 const [selectedResource, setSelectedResource] = React.useState(null);
 const [userDismissed, setUserDismissed] = React.useState(false);
 React.useEffect(() => {
 if (selectedResource || userDismissed) return;
 if (resources.length > 0) setSelectedResource(resources[0].resourceName);
 }, [resources, selectedResource, userDismissed]);

 // Look up the live row for the currently selected resource on every
 // render so the detail panel updates as data refreshes.
 const selectedRow = React.useMemo(
 () => resources.find((r) => r.resourceName === selectedResource) || null,
 [resources, selectedResource]
 );

 // ── Render ────────────────────────────────────────────────────────
 return (
 <div className="space-y-6">
 <Breadcrumbs />

 <div>
 <h1 className="text-xl font-bold tracking-tight">Cost Center</h1>
 <p className="text-sm text-gray-500 mt-0.5">
 Foundry / model inference spend — sourced from Azure Cost Management.
 </p>
 </div>

 {notice && (
 <div
 role="alert"
 className={
 notice.level ==='info'
 ?'rounded-md border border-sky-300 bg-sky-50 px-4 py-2 text-sm text-sky-900'
 : notice.level ==='warn'
 ?'rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900'
 :'rounded-md border border-rose-300 bg-rose-50 px-4 py-2 text-sm text-rose-900'
 }
 >
 {notice.text}
 </div>
 )}

 {breakdownPending ? (
 <PageLoader message="Loading cost data…" />
 ) : (
 <>
 {/* ── KPI strip ──────────────────────────────────────────
 Five at-a-glance numbers. Foundry-only (APIM gateway
 capacity has been intentionally dropped from the
 headline metrics — its fixed monthly cost was
 distorting per-call / per-token ratios). */}
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
 <ErrorBoundary>
 <KPICard
 title="Foundry Cost"
 value={foundryCost}
 format="currency"
 icon={Cpu}
 color="violet"
 badge={isEstimated ? { label:'ESTIMATED', title:'Priced from token usage × model pricing for this short range — not Azure-billed. Pick 7d+ for billed actuals.' } : undefined}
 subtitle={`AI / model inference · ${timeRange}`}
 />
 </ErrorBoundary>
 <ErrorBoundary>
 <KPICard
 title="Total Tokens"
 value={totalTokens}
 format="tokens"
 icon={Layers}
 color="brand"
 subtitle="Prompt + completion"
 />
 </ErrorBoundary>
 <ErrorBoundary>
 <KPICard
 title="Total Calls"
 value={totalCalls}
 format="number"
 icon={Activity}
 color="emerald"
 subtitle="Inference requests"
 />
 </ErrorBoundary>
 <ErrorBoundary>
 <KPICard
 title="Cost per 1K Tokens"
 value={costPer1KTokens}
 format="currency"
 icon={Calculator}
 color="violet"
 subtitle="Blended efficiency"
 />
 </ErrorBoundary>
 <ErrorBoundary>
 <KPICard
 title="Projected AI Cost"
 value={projectedCost}
 format="currency"
 icon={TrendingUp}
 color="amber"
 subtitle={`Run-rate · next ${RANGE_DAYS[timeRange] || 30}d`}
 />
 </ErrorBoundary>
 </div>

 {/* ── Cost trend (daily / weekly) + forecast ───────────
 The headline "where are we heading?" view for product
 owners. Daily/Weekly toggle over billed spend (accurate
 up to 364 days, unlike the 45-day gateway estimate), plus
 Azure's own forecast overlay and a projected month-end
 total. All billed-accurate — no token estimation here. */}
 <ErrorBoundary>
 <CostTrendSection dailySeries={dailySeries} forecast={forecast} />
 </ErrorBoundary>

 {/* ── Resource master/detail ─────────────────────────── */}
 <ResourcesSection
 resources={resources}
 totals={byResource?.totals}
 selected={selectedRow}
 onSelect={(name) => { setSelectedResource(name); setUserDismissed(false); }}
 onClose={() => { setSelectedResource(null); setUserDismissed(true); }}
 />

 {/* ── Estimated cost by APIM Product ───────────────────── 
 Computed from tokens × per-model pricing (not billed),
 so it's explicitly labelled as an estimate. */}
 <ErrorBoundary>
          <ProductCostSection
            products={Array.isArray(byProduct?.data) ? byProduct.data : []}
            totals={byProduct?.totals}
            bySubscription={Array.isArray(byProduct?.bySubscription) ? byProduct.bySubscription : []}
            subscriptionTotals={byProduct?.subscriptionTotals}
            byApp={Array.isArray(byProduct?.byApp) ? byProduct.byApp : []}
            appTotals={byProduct?.appTotals}
            unpricedModels={Array.isArray(byProduct?.unpricedModels) ? byProduct.unpricedModels : []}
          />
 </ErrorBoundary>
 </>
 )}
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 ProductCostSection
 ----------------------------------------------------------------------
 Estimated LLM spend attributed to each APIM Product. The dollar figure
 is COMPUTED (tokens × per-model pricing), not read from Azure billing —
 Cost Management can't split by APIM product — so the header carries an
 explicit"estimated" badge to avoid confusion with the billed numbers.

 Attribution is sourced directly from the APIM gateway logs
 (ApiManagementGatewayLogs), which carry first-class ProductId and
 ApimSubscriptionId columns alongside the ai-gateway token trace. This is
 exact (no Graph lookups / name matching), so both the by-Product and the
 by-Subscription tables are always populated when there's gateway traffic.
 Requests with no product are bucketed as"Other / Unassigned"; master-key
 traffic surfaces under the"Master Key (shared)" subscription row.
 ───────────────────────────────────────────────────────────────────── */
function ProductCostSection({ products, totals, bySubscription = [], subscriptionTotals, byApp = [], appTotals, unpricedModels = [] }) {
 // We render BOTH views so a decision-maker sees the complete picture:
 //   1. Estimated Cost by Product      — the chargeback rollup (ProductId)
 //   2. Estimated Cost by Subscription — the APIM subscription key breakdown
 //      (ApimSubscriptionId), which also exposes"master" / shared-key spend.
 const productRows = Array.isArray(products) ? products : [];
 const subscriptionRows = Array.isArray(bySubscription) ? bySubscription : [];
 // 3. Estimated Cost by App — the Entra app (appClientId) breakdown. This is
 //    the attribution that actually has coverage for JWT traffic, where
 //    ProductId is empty and the subscription is "master". Normalized into the
 //    shared table shape (productId/productName) so it renders identically.
 const appRows = (Array.isArray(byApp) ? byApp : []).map((a) => ({
   productId: a.appClientId,
   productName: a.appName,
   unattributed: a.unattributed,
   cost: a.cost,
   tokens: a.tokens,
   calls: a.calls,
   costPer1KTokens: a.costPer1KTokens,
   sharePct: a.sharePct,
 }));

 // Which product row is expanded for its per-model breakdown chart. Null = none.
 const [selectedProductId, setSelectedProductId] = React.useState(null);

 // Deep-link support: when arriving from the Dashboard product chart, the
 // clicked product is carried in the shared `product` filter. Auto-expand its
 // per-model breakdown once the matching row is present in the data.
 const { filters, setFilter } = useFilters() || { filters: {}, setFilter: () => {} };
 const productFilter = filters?.product || '';
 React.useEffect(() => {
 if (productFilter && productRows.some((p) => p.productId === productFilter)) {
 setSelectedProductId(productFilter);
 }
 }, [productFilter, productRows]);

 const selectedProduct = React.useMemo(
 () => productRows.find((p) => p.productId === selectedProductId) || null,
 [productRows, selectedProductId]
 );

 return (
 <div className="space-y-6">
 {/* ── Visual: Estimated cost by product ──────────────────
 A chart-first view for product owners — the tables below carry the
 exact numbers, but this answers "who is spending the most" at a
 glance. Bar chart ranks products by $; donut shows each product's
 share of total spend. Built from the same product rows the tables
 use, so the two always agree. */}
 <ProductCostChart rows={productRows} totals={totals} />

 {/* ── Attribution-window note ────────────────────────────
 Product/subscription/app attribution is derived from the APIM
 gateway logs, which the workspace retains for only ~45 days —
 far less than the 364-day billed trend above. Calling this out
 explicitly prevents the confusion of "why does the billed trend
 go back further than the per-product breakdown?" and flags that
 long windows understate per-product spend. */}
 <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3">
 <AlertTriangle className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
 <div className="text-sm text-sky-900 min-w-0">
 <p className="font-semibold">Attribution covers the last ~45 days of gateway logs</p>
 <p className="text-sky-800 mt-0.5">
 Per-product, per-subscription and per-app figures are computed from
 the APIM gateway logs (tokens × pricing), which are retained for
 about <span className="font-medium">45 days</span>. The billed
 <span className="font-medium"> Spend Trend</span> and
 <span className="font-medium"> Forecast</span> above come from Azure
 Cost Management and cover up to <span className="font-medium">364 days</span>,
 so a longer time range will show more billed history than
 product-level attribution. For full per-product history over long
 windows, use the daily product-cost snapshots (coming soon).
 </p>
 </div>
 </div>

 {/* ── Unpriced-model warning ─────────────────────────────
 When some models with usage have no configured pricing tier, their
 cost is counted as $0 — understating the estimate. Surface which
 models so an admin can add pricing in Settings. */}
 {unpricedModels.length > 0 && (
 <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
 <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
 <div className="text-sm text-amber-900 min-w-0">
 <p className="font-semibold">
 {unpricedModels.length} model{unpricedModels.length > 1 ?'s' :''} missing pricing — estimate understated
 </p>
 <p className="text-amber-800 mt-0.5">
 The models below have usage but no configured price, so their cost
 is counted as $0. Add pricing in <span className="font-medium">Settings → Model Pricing</span> for an accurate estimate.
 </p>
 <div className="flex flex-wrap gap-1.5 mt-2">
 {unpricedModels.map((m) => (
 <span
 key={m.model}
 className="font-mono text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200"
 title={`${(m.tokens || 0).toLocaleString()} tokens · ${(m.calls || 0).toLocaleString()} calls`}
 >
 {m.model}
 </span>
 ))}
 </div>
 </div>
 </div>
 )}

 {/* ── By Product ─────────────────────────────────────── */}
 <CostAttributionTable
 title="Estimated Cost by Product"
 subtitle="Attributed via ApiManagementGatewayLogs ProductId — tokens × per-model pricing. Click a product to see its per-model breakdown."
 entityLabel="Product"
 rows={productRows}
 totals={totals}   exportName="cost-by-product"
   emptyMessage="No product-attributable gateway traffic in this window."
   onRowClick={(id) => { setSelectedProductId(id); setFilter('product', id || ''); }}
   selectedId={selectedProductId}
  />

  {/* Per-model breakdown for the selected product. */}
  {selectedProduct && (
  <ProductModelBreakdown
  product={selectedProduct}
  onClose={() => { setSelectedProductId(null); setFilter('product', ''); }}
  />
 )}

 {/* ── By APIM Subscription ──────────────────────────── */}
 <CostAttributionTable
 title="Estimated Cost by APIM Subscription"
 subtitle="Attributed via ApimSubscriptionId. Shared-key traffic appears as “master”."
 entityLabel="Subscription"
 rows={subscriptionRows}
 totals={subscriptionTotals}
 exportName="cost-by-subscription"
 emptyMessage="No subscription-attributable gateway traffic in this window."
 />

 {/* ── By App (Entra client) ─────────────────────────── */}
 <CostAttributionTable
 title="Estimated Cost by App"
 subtitle="Attributed via the calling Entra app (appClientId) from the ai-gateway trace — the attribution with coverage for JWT traffic where no product/subscription is attached."
 entityLabel="App"
 rows={appRows}
 totals={appTotals}
 exportName="cost-by-app"
 emptyMessage="No app-attributable gateway traffic in this window."
 />
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 CostTrendSection
 ----------------------------------------------------------------------
 Billed-spend trend for product owners, with a Daily / Weekly toggle and
 an optional Azure forecast overlay. All figures are BILLED (Azure Cost
 Management) — accurate up to ~364 days — so this section is trustworthy
 over long ranges, unlike the token-based per-product estimate which is
 capped at ~45 days of gateway logs.

 • Daily  — the raw per-day series (`dailySeries`, from /costs/breakdown).
 • Weekly — the same series rolled up into ISO weeks, which smooths out
            weekday/weekend noise so a multi-week trend is legible.
 • Forecast — Azure's actual+projected series (/costs/forecast). When
              present we surface the projected month-end total as a KPI and
              plot the actual-vs-forecast line so owners see where spend is
              heading, not just where it's been.
 ───────────────────────────────────────────────────────────────────── */
function CostTrendSection({ dailySeries = [], forecast }) {
 const [grain, setGrain] = React.useState('daily'); // 'daily' | 'weekly'

 // Currency tick/tooltip formatters so the axes read as dollars, not raw
 // counts (AreaChart defaults to compact numbers).
 const fmtAxis = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
 const fmtTip = (v) => formatCurrency(Number(v) || 0);

 // Roll the daily series up into ISO-week buckets (week starting Monday).
 const weeklySeries = React.useMemo(() => {
 const byWeek = new Map();
 for (const d of dailySeries) {
 const dt = new Date(d.TimeSlice || d.date);
 if (isNaN(dt.getTime())) continue;
 // Monday-anchored week key.
 const day = (dt.getUTCDay() + 6) % 7; // 0 = Monday
 const monday = new Date(dt);
 monday.setUTCDate(dt.getUTCDate() - day);
 const key = monday.toISOString().slice(0, 10);
 const prev = byWeek.get(key) || { TimeSlice: `${key}T00:00:00Z`, date: key, Cost: 0 };
 prev.Cost += Number(d.Cost) || 0;
 byWeek.set(key, prev);
 }
 return Array.from(byWeek.values())
 .map((w) => ({ ...w, Cost: Math.round(w.Cost * 1000000) / 1000000 }))
 .sort((a, b) => a.date.localeCompare(b.date));
 }, [dailySeries]);

 const series = grain === 'weekly' ? weeklySeries : dailySeries;

 // Azure forecast overlay: split into actual vs forecast so the chart can
 // render them as two series (solid history, projected tail).
 const forecastRows = React.useMemo(() => {
 const rows = Array.isArray(forecast?.forecast) ? forecast.forecast : [];
 return rows.map((r) => ({
 TimeSlice: `${r.date}T00:00:00Z`,
 date: r.date,
 Actual: r.type === 'actual' ? (Number(r.value) || 0) : null,
 Forecast: r.type === 'forecast' ? (Number(r.value) || 0) : null,
 }));
 }, [forecast]);

 const projected = forecast?.totalProjected;
 const forecastUnavailable = !!forecast?.unavailable;
 const hasForecast = forecastRows.length > 0 && !forecastUnavailable;

 if (dailySeries.length === 0 && !hasForecast) return null;

 return (
 <div className="space-y-6">
 {/* Billed trend with grain toggle. */}
 {series.length > 0 && (
 <div className="chart-container">
 <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
 <div>
 <div className="flex items-center gap-2">
 <h2 className="text-sm font-semibold text-gray-700">Spend Trend</h2>
 <span
 className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800"
 title="Actual spend from Azure Cost Management — billed, not estimated. Accurate up to ~364 days."
 >
 Billed
 </span>
 </div>
 <p className="text-xs text-gray-500 mt-0.5">
 {grain === 'weekly'
 ? 'Weekly billed spend (ISO weeks, Monday-anchored). Smooths daily noise for a clearer trend.'
 : 'Daily billed spend. Is spend stable, climbing, or spiking?'}
 </p>
 </div>
 <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs font-medium">
 {['daily', 'weekly'].map((g) => (
 <button
 key={g}
 type="button"
 onClick={() => setGrain(g)}
 className={
 grain === g
 ? 'px-3 py-1.5 bg-brand-500 text-white'
 : 'px-3 py-1.5 bg-white text-gray-600 hover:bg-gray-50'
 }
 >
 {g === 'daily' ? 'Daily' : 'Weekly'}
 </button>
 ))}
 </div>
 </div>
 <AreaChart
 data={series}
 dataKeys={['Cost']}
 xAxisKey="TimeSlice"
 yAxisLabel="Spend (USD)"
 formatYAxis={fmtAxis}
 formatTooltip={fmtTip}
 height={240}
 exportFilename={`cost-trend-${grain}`}
 seriesLabels={{ Cost: 'Billed spend' }}
 />
 </div>
 )}

 {/* Azure forecast overlay + projected month-end total. */}
 {hasForecast && (
 <div className="chart-container">
 <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
 <div>
 <div className="flex items-center gap-2">
 <h2 className="text-sm font-semibold text-gray-700">Actual vs Forecast</h2>
 <span
 className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-800"
 title="Azure Cost Management's own forecast for the resource group. Billed history plus a projected tail."
 >
 Azure forecast
 </span>
 </div>
 <p className="text-xs text-gray-500 mt-0.5">
 Where spend is heading — Azure's projection based on recent billed history.
 </p>
 </div>
 {projected != null && (
 <div className="text-right">
 <div className="text-lg font-semibold text-gray-900 tabular-nums">
 {formatCurrency(projected)}
 </div>
 <div className="text-[11px] text-gray-500 uppercase tracking-wider">Projected total</div>
 </div>
 )}
 </div>
 <AreaChart
 data={forecastRows}
 dataKeys={['Actual', 'Forecast']}
 xAxisKey="TimeSlice"
 yAxisLabel="Spend (USD)"
 formatYAxis={fmtAxis}
 formatTooltip={fmtTip}
 height={240}
 seriesLabels={{ Actual: 'Actual (billed)', Forecast: 'Forecast (projected)' }}
 exportFilename="cost-actual-vs-forecast"
 />
 </div>
 )}
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 ProductCostChart
 ----------------------------------------------------------------------
 Chart-first view of estimated cost by product, for product owners.
 Left: horizontal bar chart ranking products by $ (top N). Right: a
 donut showing each product's share of total spend. Both are built from
 the same `rows` the tables use, so numbers always reconcile. Small
 products are auto-grouped into "Other" by the PieChart component; the
 bar chart shows the top N by cost.
 ───────────────────────────────────────────────────────────────────── */
function ProductCostChart({ rows = [], totals }) {
 // Only products with real spend belong on the chart. Keep the top 10 by
 // cost for the bar chart so labels stay legible; the donut handles its own
 // "Other" grouping for the long tail.
 const chartRows = React.useMemo(() => {
 return (Array.isArray(rows) ? rows : [])
 .filter((r) => (Number(r.cost) || 0) > 0)
 .map((r) => ({
 name: r.productName || r.productId,
 cost: Math.round((Number(r.cost) || 0) * 100) / 100,
 sharePct: Number(r.sharePct) || 0,
 }))
 .sort((a, b) => b.cost - a.cost);
 }, [rows]);

 const barRows = React.useMemo(() => chartRows.slice(0, 10), [chartRows]);

 // Nothing to plot — let the tables below carry the empty-state message.
 if (chartRows.length === 0) return null;

 return (
 <div className="space-y-2">
 <div className="flex items-center justify-between gap-2 flex-wrap">
 <div>
 <div className="flex items-center gap-2">
 <h2 className="text-sm font-semibold text-gray-700">Estimated Cost by Product</h2>
 <span
 className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
 title="Computed from tokens × per-model pricing — not Azure-billed."
 >
 Estimated
 </span>
 </div>
 <p className="text-xs text-gray-500 mt-0.5">
 Ranked by estimated spend. Hover a bar or slice for details.
 </p>
 </div>
 {totals && (
 <div className="text-right">
 <div className="text-lg font-semibold text-gray-900 tabular-nums">
 {formatCurrency(totals.cost)}
 </div>
 <div className="text-[11px] text-gray-500 uppercase tracking-wider">Total</div>
 </div>
 )}
 </div>

 <div className="grid gap-6 lg:grid-cols-5">
 {/* Bar: top products by $ */}
 <div className="lg:col-span-3">
 <BarChart
 data={barRows}
 dataKeys={['cost']}
 xAxisKey="name"
 horizontal
 colorByIndex
 showValueLabels
 valueFormat="currency"
 height={Math.max(220, barRows.length * 40)}
 exportFilename="cost-by-product-chart"
 />
 </div>
 {/* Donut: share of total spend */}
 <div className="lg:col-span-2">
 <PieChart
 data={chartRows}
 dataKey="cost"
 nameKey="name"
 height={280}
 exportFilename="cost-by-product-share"
 />
 </div>
 </div>
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 ProductModelBreakdown
 ----------------------------------------------------------------------
 Per-model cost breakdown for ONE selected product. Shown when a product
 row is clicked. Bar chart ranks the product's models by $, alongside a
 small table with tokens/calls so a product owner sees exactly which
 models drive their spend. Built from the row's own `byModel` array (the
 backend already attaches it), so no extra fetch is needed.
 ───────────────────────────────────────────────────────────────────── */
function ProductModelBreakdown({ product, onClose }) {
 const models = Array.isArray(product?.byModel) ? product.byModel : [];
 const chartData = React.useMemo(
 () => models
 .map((m) => ({ name: m.model || 'unknown', cost: Math.round((Number(m.cost) || 0) * 100) / 100 }))
 .filter((m) => m.cost > 0)
 .sort((a, b) => b.cost - a.cost),
 [models]
 );

 return (
 <div className="chart-container border-l-2 border-brand-500">
 <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
 <div>
 <h3 className="text-sm font-semibold text-gray-800">
 Per-model cost — <span className="text-brand-600">{product.productName || product.productId}</span>
 </h3>
 <p className="text-xs text-gray-500 mt-0.5">
 Which models this product’s spend is going to. Total {formatCurrency(product.cost)} · {formatTokens(product.tokens)} tokens · {Number(product.calls || 0).toLocaleString()} calls.
 </p>
 </div>
 <button
 type="button"
 onClick={onClose}
 className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 transition"
 >
 <X className="w-3.5 h-3.5" /> Close
 </button>
 </div>

 {chartData.length === 0 ? (
 <div className="px-2 py-8 text-center text-sm text-gray-400">
 No priced model usage for this product in the window.
 </div>
 ) : (
 <div className="grid gap-6 lg:grid-cols-5">
 <div className="lg:col-span-3">
 <BarChart
 data={chartData}
 dataKeys={['cost']}
 xAxisKey="name"
 horizontal
 colorByIndex
 showValueLabels
 valueFormat="currency"
 height={Math.max(200, chartData.length * 38)}
 exportFilename={`cost-${(product.productName || product.productId || 'product')}-by-model`}
 />
 </div>
 <div className="lg:col-span-2 overflow-x-auto">
 <table className="min-w-full text-sm">
 <thead className="bg-gray-50">
 <tr>
 <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Model</th>
 <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">Tokens</th>
 <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">Est. Cost</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100">
 {models
 .slice()
 .sort((a, b) => (Number(b.cost) || 0) - (Number(a.cost) || 0))
 .map((m) => (
 <tr key={m.model} className="hover:bg-gray-50">
 <td className="px-3 py-1.5 font-mono text-xs text-gray-800 truncate max-w-[160px]" title={m.model}>{m.model}</td>
 <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{formatTokens(m.tokens)}</td>
 <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900">{formatCurrency(Number(m.cost) || 0)}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 )}
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 CostAttributionTable
 ----------------------------------------------------------------------
 Reusable table for an "estimated cost" breakdown (by product OR by
 two sections read consistently when presented side-by-side.
 ───────────────────────────────────────────────────────────────────── */
function CostAttributionTable({ title, subtitle, entityLabel, rows = [], totals, exportName, emptyMessage, onRowClick, selectedId }) {
 const topCost = React.useMemo(
 () => Math.max(1, ...rows.map((p) => Number(p.cost) || 0)),
 [rows]
 );

 return (
 <div className="space-y-2">
 <div className="flex items-center justify-between gap-2 flex-wrap">
 <div>
 <div className="flex items-center gap-2">
 <h2 className="text-sm font-semibold text-gray-700">
 {title}
 </h2>
 <span
 className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded
 bg-amber-100 text-amber-800"
 title="Computed from tokens × per-model pricing — not Azure-billed. Cost Management cannot split cost by APIM product."
 >
 Estimated
 </span>
 </div>
 <p className="text-xs text-gray-500 mt-0.5">
 {subtitle}
 </p>
 </div>
 <ExportButton data={rows} filename={exportName} />
 </div>

 {rows.length === 0 ? (
 <div className="chart-container">
 <div className="px-2 py-8 text-center text-sm text-gray-400">
 {emptyMessage}
 </div>
 </div>
 ) : (
 <div className="chart-container p-0 overflow-hidden">
 <div className="overflow-x-auto">
 <table className="min-w-full text-sm">
 <thead className="bg-gray-50">
 <tr>
 <th scope="col" className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">{entityLabel}</th>
 <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">Calls</th>
 <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">Tokens</th>
 <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">$/1K</th>
 <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 min-w-[180px]">Est. Cost</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100">
 {rows.map((p) => {
 const cost = Number(p.cost) || 0;
 const widthPct = Math.max(2, (cost / topCost) * 100);
 const isSpecial = p.unattributed || p.master;
 const clickable = typeof onRowClick === 'function' && Array.isArray(p.byModel) && p.byModel.length > 0;
 const isSelected = clickable && selectedId === p.productId;
 return (
 <tr
 key={p.productId}
 onClick={clickable ? () => onRowClick(isSelected ? null : p.productId) : undefined}
 className={`transition-colors ${clickable ? 'cursor-pointer' : ''} ${
 isSelected
 ? 'bg-brand-50 hover:bg-brand-50'
 : isSpecial ?'bg-amber-50/40 hover:bg-amber-50/70' :'hover:bg-gray-50'
 }`}
 title={clickable ? 'Click to see this row’s per-model cost breakdown' : undefined}
 >
 <td className="px-4 py-2 font-medium text-gray-900 truncate max-w-[240px]" title={p.productName}>
 <span className="inline-flex items-center gap-1.5">
 {clickable && (
 <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
 )}
 {p.productName || p.productId}
 {isSpecial && (
 <span
 className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
 title={p.unattributed
 ?'Gateway requests with no ProductId set — could not be mapped to an APIM product.'
 :'Traffic using the built-in APIM master (shared) subscription key.'}
 >
 {p.unattributed ?'Unmapped' :'Shared'}
 </span>
 )}
 </span>
 </td>
 <td className="px-4 py-2 text-right tabular-nums text-gray-700">
 {Number(p.calls || 0).toLocaleString()}
 </td>
 <td className="px-4 py-2 text-right tabular-nums text-gray-700">
 {formatTokens(p.tokens)}
 </td>
 <td className="px-4 py-2 text-right tabular-nums text-gray-700">
 {p.costPer1KTokens != null ? formatCurrency(p.costPer1KTokens, 4) :'—'}
 </td>
 <td className="px-4 py-2">
 <div className="flex items-center gap-3 justify-end">
 <div className="flex-1 max-w-[120px] h-1.5 rounded-full bg-gray-100 overflow-hidden">
 <div className="h-full rounded-full bg-brand-500" style={{ width:`${widthPct}%` }} />
 </div>
 <span className="tabular-nums font-semibold text-gray-900 min-w-[64px] text-right">
 {formatCurrency(cost)}
 </span>
 <span className="tabular-nums text-xs text-gray-500 min-w-[40px] text-right">
 {Number(p.sharePct || 0).toFixed(1)}%
 </span>
 </div>
 </td>
 </tr>
 );
 })}
 </tbody>
 {totals && (
 <tfoot>
 <tr className="border-t border-gray-200 bg-gray-50/80">
 <td className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Total ({rows.length})
 </td>
 <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">
 {Number(totals.calls || 0).toLocaleString()}
 </td>
 <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">
 {formatTokens(totals.tokens)}
 </td>
 <td />
 <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">
 {formatCurrency(totals.cost)}
 </td>
 </tr>
 </tfoot>
 )}
 </table>
 </div>
 </div>
 )}
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 ResourcesSection
 ----------------------------------------------------------------------
 Master-detail layout. On the left: a list of every Foundry resource
 in the resource group, with sparkline + cost. On the right: when one
 is selected, a detail panel showing model breakdown and per-model
 efficiency. On narrow viewports the panel stacks below the list.
 ───────────────────────────────────────────────────────────────────── */
function ResourcesSection({ resources, totals, selected, onSelect, onClose }) {
 const [query, setQuery] = React.useState('');

 const filtered = React.useMemo(() => {
 const q = query.trim().toLowerCase();
 if (!q) return resources;
 return resources.filter((r) => r.resourceName.toLowerCase().includes(q));
 }, [resources, query]);

 return (
 <div className="space-y-2">
 <div className="flex items-center justify-between">
 <div>
 <h2 className="text-sm font-semibold text-gray-700">
 Foundry Resources
 </h2>
 <p className="text-xs text-gray-500 mt-0.5">
 Click a resource to see per-model breakdown.
 </p>
 </div>
 <div className="flex items-center gap-2">
 <div className="relative">
 <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
 <input
 type="text"
 placeholder="Filter resources…"
 value={query}
 onChange={(e) => setQuery(e.target.value)}
 className="pl-8 pr-3 py-1.5 text-xs rounded-md border border-gray-300 bg-white
 text-gray-700 
 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500
 w-48"
 />
 </div>
 <ExportButton data={resources} filename="cost-by-resource" />
 </div>
 </div>

 <div className={`grid gap-4 ${selected ?'lg:grid-cols-5' :'grid-cols-1'}`}>
 {/* List */}
 <div className={selected ?'lg:col-span-2' :'col-span-1'}>
 <ResourceList
 resources={filtered}
 totals={totals}
 selectedName={selected?.resourceName}
 onSelect={onSelect}
 />
 </div>

 {/* Detail panel */}
 {selected && (
 <div className="lg:col-span-3">
 <ResourceDetail resource={selected} onClose={onClose} />
 </div>
 )}
 </div>
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 ResourceList — left rail of the master/detail.
 Each row is a clickable card with: name, total cost, share of total,
 token count, and a 7-day cost sparkline for trend-at-a-glance.
 ───────────────────────────────────────────────────────────────────── */
function ResourceList({ resources, totals, selectedName, onSelect }) {
 if (!resources || resources.length === 0) {
 return (
 <div className="chart-container">
 <div className="px-2 py-8 text-center text-sm text-gray-400">
 No Foundry resources have any spend in this window.
 </div>
 </div>
 );
 }

 return (
 <div className="chart-container p-0 overflow-hidden">
 <ul
 role="listbox"
 aria-label="Foundry resources"
 className="divide-y divide-gray-100 max-h-[640px] overflow-y-auto"
 >
 {resources.map((r) => {
 const active = r.resourceName === selectedName;
 return (
 <li key={r.resourceName}>
 <button
 type="button"
 role="option"
 aria-selected={active}
 onClick={() => onSelect(r.resourceName)}
 className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors
 ${active
 ?'bg-brand-50 border-l-2 border-brand-500'
 :'hover:bg-gray-50 border-l-2 border-transparent'
 }`}
 >
 <div className={`p-2 rounded-md flex-shrink-0
 ${active
 ?'bg-brand-100 text-brand-600'
 :'bg-gray-100 text-gray-500'
 }`}
 >
 <Server className="w-4 h-4" />
 </div>

 <div className="flex-1 min-w-0">
 <div className="flex items-center justify-between gap-2">
 <span className="font-mono text-sm font-medium text-gray-900 truncate">
 {r.resourceName}
 </span>
 <span className="text-sm font-semibold tabular-nums text-gray-900">
 {formatCurrency(r.cost)}
 </span>
 </div>
 <div className="flex items-center justify-between gap-2 mt-1 text-xs text-gray-500">
 <span className="tabular-nums">
 {formatTokens(r.tokens)} tokens · {(r.modelCount || 0)} model{r.modelCount === 1 ?'' :'s'}
 </span>
 <span className="tabular-nums">
 {Number(r.sharePct || 0).toFixed(1)}%
 </span>
 </div>
 </div>

 <div className="flex items-center gap-2 flex-shrink-0">
 <ChevronRight className={`w-4 h-4 transition-transform
 ${active ?'text-brand-500 translate-x-0.5' :'text-gray-300'}`}
 />
 </div>
 </button>
 </li>
 );
 })}
 </ul>

 {totals && (
 <div className="px-4 py-2.5 border-t border-gray-200 
 bg-gray-50/80 flex items-center justify-between text-xs">
 <span className="font-semibold uppercase tracking-wider text-gray-500">
 Total ({resources.length})
 </span>
 <span className="font-semibold tabular-nums text-gray-900">
 {formatCurrency(totals.cost)}
 </span>
 </div>
 )}
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 ResourceDetail — right pane of the master/detail.
 Per-resource KPIs, daily trend area chart, and a per-model table.
 ───────────────────────────────────────────────────────────────────── */
function ResourceDetail({ resource, onClose }) {
 const daily = React.useMemo(
 () => (resource.daily || []).map((d) => ({
 TimeSlice:`${d.date}T00:00:00Z`,
 Cost: d.cost,
 })),
 [resource]
 );

 const byModel = React.useMemo(
 () => Array.isArray(resource.byModel) ? resource.byModel : [],
 [resource]
 );

 // Top-of-panel KPIs — same shape as the global KPI strip so users
 // can mentally compare"this resource" vs."everything" at a glance.
 const tiles = [
 { label:'Cost', value: formatCurrency(resource.cost) },
 { label:'Tokens', value: formatTokens(resource.tokens) },
 { label:'Calls', value: Number(resource.calls || 0).toLocaleString() },
 {
 label:'$/1K tokens',
 value: resource.costPer1KTokens != null
 ? formatCurrency(resource.costPer1KTokens, 4)
 :'—',
 },
 ];

 return (
 <div className="chart-container">
 <div className="flex items-start justify-between mb-4">
 <div className="min-w-0">
 <div className="flex items-center gap-2 mb-1">
 <Server className="w-4 h-4 text-brand-500" />
 <h3 className="font-mono text-base font-semibold text-gray-900 truncate">
 {resource.resourceName}
 </h3>
 </div>
 <p className="text-xs text-gray-500">
 {resource.modelCount || 0} model{resource.modelCount === 1 ?'' :'s'} ·
 {''}{Number(resource.sharePct || 0).toFixed(1)}% of total Foundry spend
 </p>
 </div>
 <button
 type="button"
 onClick={onClose}
 aria-label="Close detail"
 className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100
 transition-colors"
 >
 <X className="w-4 h-4" />
 </button>
 </div>

 {/* Resource-scoped KPI tiles. Compact (no full KPICard chrome)
 because we're inside an already-bordered card. */}
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
 {tiles.map((t) => (
 <div
 key={t.label}
 className="rounded-lg bg-gray-50 px-3 py-2.5
 border border-gray-200/60"
 >
 <div className="text-[10px] font-semibold uppercase tracking-wider
 text-gray-500">
 {t.label}
 </div>
 <div className="text-base font-bold tabular-nums text-gray-900 mt-0.5">
 {t.value}
 </div>
 </div>
 ))}
 </div>

 {/* Daily trend for THIS resource. Same chart component as the
 global one, just scoped — gives users the same visual
 vocabulary at both levels. */}
 {daily.length > 1 && (
 <div className="mb-5">
 <AreaChart
 data={daily}
 dataKeys={['Cost']}
 xAxisKey="TimeSlice"
 title="Daily Cost"
 height={180}
 />
 </div>
 )}

 {/* Per-model breakdown table. The bar in the"Cost" column is a
 pure-CSS in-row chart, which is faster to scan than a
 separate horizontal bar chart and keeps all the model data
 in one place. */}
 <div>
 <div className="flex items-center justify-between mb-2">
 <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 Cost Distribution by Model
 </h4>
 <ExportButton
 data={byModel}
 filename={`cost-${resource.resourceName}-by-model`}
 />
 </div>
 <ModelBreakdownTable rows={byModel} resourceCost={resource.cost} />
 </div>
 </div>
 );
}

/* ─────────────────────────────────────────────────────────────────────────
 ModelBreakdownTable
 Compact per-model table with an inline bar visualising the share of
 the resource's spend each model is responsible for.
 ───────────────────────────────────────────────────────────────────── */
function ModelBreakdownTable({ rows, resourceCost }) {
 if (!rows || rows.length === 0) {
 return (
 <div className="rounded-lg border border-gray-200 px-4 py-6
 text-center text-sm text-gray-400">
 No model-level data available for this resource.
 </div>
 );
 }

 // Find the top cost so the inline bars are normalised to"biggest
 // model on this resource = 100%". Using the biggest model (not the
 // resource total) makes the bars more visually distinct when one
 // model dominates spend — common with Foundry deployments.
 const topCost = Math.max(...rows.map((r) => Number(r.cost) || 0)) || 1;

 return (
 <div className="overflow-x-auto rounded-lg border border-gray-200">
 <table className="min-w-full text-sm">
 <thead className="bg-gray-50">
 <tr>
 <th scope="col" className="px-4 py-2 text-left text-[10px] font-semibold
 uppercase tracking-wider text-gray-500">
 Model
 </th>
 <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold
 uppercase tracking-wider text-gray-500">
 Calls
 </th>
 <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold
 uppercase tracking-wider text-gray-500">
 Tokens
 </th>
 <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold
 uppercase tracking-wider text-gray-500">
 $/1K tokens
 </th>
 <th scope="col" className="px-4 py-2 text-right text-[10px] font-semibold
 uppercase tracking-wider text-gray-500 min-w-[160px]">
 Cost
 </th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100">
 {rows.map((r) => {
 const cost = Number(r.cost) || 0;
 const widthPct = Math.max(2, (cost / topCost) * 100);
 return (
 <tr
 key={r.model}
 className="hover:bg-gray-50 transition-colors"
 >
 <td className="px-4 py-2 font-mono text-[13px] text-gray-900">
 {r.model ||'unknown'}
 </td>
 <td className="px-4 py-2 text-right tabular-nums text-gray-700">
 {Number(r.calls || 0).toLocaleString()}
 </td>
 <td className="px-4 py-2 text-right tabular-nums text-gray-700">
 {formatTokens(r.tokens)}
 </td>
 <td className="px-4 py-2 text-right tabular-nums text-gray-700">
 {r.costPer1KTokens != null
 ? formatCurrency(r.costPer1KTokens, 4)
 :'—'}
 </td>
 <td className="px-4 py-2">
 <div className="flex items-center gap-3 justify-end">
 <div className="flex-1 max-w-[120px] h-1.5 rounded-full
 bg-gray-100 overflow-hidden">
 <div
 className="h-full rounded-full bg-brand-500"
 style={{ width:`${widthPct}%` }}
 />
 </div>
 <span className="tabular-nums font-semibold text-gray-900 
 min-w-[64px] text-right">
 {formatCurrency(cost)}
 </span>
 <span className="tabular-nums text-xs text-gray-500 
 min-w-[40px] text-right">
 {Number(r.sharePct || (resourceCost > 0 ? (cost / resourceCost) * 100 : 0)).toFixed(1)}%
 </span>
 </div>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 );
}

// ── Formatters ─────────────────────────────────────────────────────────
function formatCurrency(value, decimals = 2) {
 const n = Number(value);
 if (!isFinite(n)) return'—';
 if (n !== 0 && Math.abs(n) < 0.01 && decimals < 4) {
 return`$${n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
 }
 return`$${n.toLocaleString(undefined, {
 minimumFractionDigits: decimals,
 maximumFractionDigits: decimals,
 })}`;
}

function formatTokens(value) {
 const n = Number(value) || 0;
 if (n >= 1_000_000) return`${(n / 1_000_000).toFixed(1)}M`;
 if (n >= 1_000) return`${(n / 1_000).toFixed(1)}K`;
 return n.toLocaleString();
}
